import { whiteboardCreated, whiteboardOpened } from 'features/layout/features/content/actions';
import { WhiteboardCreatedOptions } from 'features/layout/features/content/types';
import { openModal } from 'features/modal/modalSlice';
import { selectWhiteboardId, selectWhiteboardOpen } from 'features/whiteboard/whiteboardSlice';
import { useWhiteboard } from 'features/whiteboard/hooks/useWhiteboard';
import { whiteboardModeChanged } from 'features/whiteboard/actions';
import { selectWhiteboardIsRestorable } from 'features/whiteboard/drafts/whiteboardDraftsSlice';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';
import { useFeatureFlag } from 'features/feature-set/useFeatureFlag';
import { getDontShowAgainPreference } from 'utils/dontShowAgain';
import {
  selectActiveFolderId,
  selectActiveLibraryType,
} from 'features/content-library/contentLibrarySlice';

export const useWhiteboardControl = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('room');

  const whiteboardOpen = useAppSelector(selectWhiteboardOpen);
  const currentWhiteboardId = useAppSelector(selectWhiteboardId);

  const activeFolderId = useAppSelector(selectActiveFolderId);
  const activeLibraryType = useAppSelector(selectActiveLibraryType);

  const { hasPermissions } = usePermissions();

  const { isFeatureAvailable, canManagePrivateWhiteboards, canEditWhiteboard } = useWhiteboard();

  const isContentLibraryEnabled = useFeatureFlag('contentLibrary');
  const canPresentContentLibrary = hasPermissions(PermissionTypes.presentContentLibrary);

  const isContentLibraryFeatureAvailable =
    isContentLibraryEnabled && (canPresentContentLibrary || canEditWhiteboard);

  const whiteboardIsRestorable = useAppSelector(selectWhiteboardIsRestorable);

  const creatingLosesWork = !isContentLibraryFeatureAvailable || whiteboardIsRestorable;

  const label = t('toolbar.create_new_whiteboard');
  const publicLabel = t('toolbar.create_public_whiteboard');
  const privateLabel = t('toolbar.create_private_whiteboard');

  const icon = 'whiteboard-create';
  const privateIcon = 'whiteboard-eye';

  const openWhiteboard = (id: string) => {
    if (!whiteboardOpen || currentWhiteboardId !== id) {
      dispatch(whiteboardOpened(id));
    }
  };

  const createWhiteboard = (params?: Partial<WhiteboardCreatedOptions>) => {
    const options: WhiteboardCreatedOptions = {
      folderId: activeFolderId === 'root' ? undefined : activeFolderId,
      personal: activeLibraryType === 'personal',
      ...params,
    };

    if (creatingLosesWork && whiteboardOpen) {
      dispatch(openModal('createNewWhiteboardConfirmation', { options }));
    } else {
      dispatch(whiteboardCreated(options));
    }
  };

  const toggleWhiteboardMode = (id: string, isPrivate: boolean) => {
    const modalName = isPrivate
      ? 'makeWhiteboardPublicConfirmation'
      : 'makeWhiteboardPrivateConfirmation';

    const dontShowAgain = getDontShowAgainPreference(modalName);

    if (dontShowAgain) {
      dispatch(whiteboardModeChanged({ id, private: !isPrivate }));
    } else {
      dispatch(openModal(modalName, { whiteboardId: id }));
    }
  };

  return {
    active: whiteboardOpen,
    label,
    publicLabel,
    privateLabel,
    icon,
    privateIcon,
    openWhiteboard,
    createWhiteboard,
    toggleWhiteboardMode,
    isFeatureAvailable,
    canManagePrivateWhiteboards,
  };
};
